import { View, Text, StyleSheet, ScrollView, Pressable } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { AUDIO_LANGUAGES, Colors, SELECT_STYLES } from "@/constants";
import PrimaryButton from "@/ui/PrimaryButton";
import { useState } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import Input from "@/ui/Input";
import { User } from "@/models/types";
import Icon from "./Icon";
import toast from "react-hot-toast";

const AlertWidgetAIContentTemplates = () => {
  const visible = useUISlice((state) => state.alertWidgetAIContentTemplates);
  const setVisible = useUISlice(
    (state) => state.setAlertWidgetAIContentTemplates
  );
  const height = useUISlice((state) => state.device.height);
  const aiTemplates = useUserSlice((state) => state.aiTemplates);
  const width = useUISlice((state) => state.device.width);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<
    User["listTemplates"][number] | undefined
  >();
  const defaultLanguage = useUserSlice(
    (state) => state.userData?.preferences?.transcriptionLanguage
  );
  const [language, setLanguage] = useState<string>(
    defaultLanguage || AUDIO_LANGUAGES[0].value
  );
  const savedTemplates = useUserSlice((state) =>
    state.userData?.listTemplates?.filter(
      (template) => template.type === "user_template"
    )
  );
  const { t } = useTranslation();

  const onSaveTemplate = async () => {
    if (!selectedTemplate?.title) {
      toast.error(t("alertWidgetAIContentTemplates.titleRequired"));
      return;
    }

    if (!selectedTemplate?.prompt || selectedTemplate.prompt.length < 10) {
      toast.error(t("alertWidgetAIContentTemplates.promptTooShort"));
      return;
    }

    const payload = {
      id: selectedTemplate.id || Date.now().toString(),
      type: selectedTemplate.type || "user_template",
      emoji: selectedTemplate.emoji || "🍋",
      title: selectedTemplate.title,
      prompt: selectedTemplate.prompt,
      language: selectedTemplate.language || language,
      createdAt: selectedTemplate.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setIsLoading(true);
    await useUserSlice.getState().user().saveTemplate(payload);
    setIsLoading(false);
    setSelectedTemplate(undefined);
    setVisible(undefined);
  };

  const onDeleteTemplate = (template: User["listTemplates"][number]) => {
    useUserSlice
      .getState()
      .user()
      .deleteTemplate(template.id, () => {
        visible?.onDeleteCallback?.();
      });
  };

  if (!visible) return null;

  return (
    <ModalContainer
      shouldPersistBackdropClick
      title={t("alertWidgetAIContentTemplates.title")}
      onDismiss={() => setVisible(undefined)}
      hideCancel
      modalWidth={Math.min(width, 600)}
      statusBarBgColor="#989898"
    >
      <ScrollView
        style={{
          marginHorizontal: -ms(24),
          flex: 1,
          minHeight: height * 0.65,
          maxHeight: height * 0.65,
        }}
      >
        <>
          <View style={styles.spacer} />
          <Text style={styles.label}>
            {t("alertWidgetAIContentTemplates.prebuiltTemplates")}
          </Text>
          <ScrollView
            contentContainerStyle={{ paddingHorizontal: ms(12) }}
            horizontal
            showsHorizontalScrollIndicator={false}
          >
            {aiTemplates.map((template) => (
              <AlertWidgetAIContentTemplates.TemplateItem
                key={template.id}
                template={{
                  onPress: () => {
                    setSelectedTemplate({
                      id: template.id,
                      type: "user_template",
                      emoji: template.emoji,
                      title: template.title,
                      prompt: template.prompt,
                      language,
                      createdAt: new Date().toISOString(),
                    });
                  },
                  emoji: template.emoji,
                  title: template.title,
                }}
              />
            ))}
          </ScrollView>

          {savedTemplates?.length > 0 && (
            <>
              <View style={styles.largerSpacer} />
              <Text style={styles.label}>
                {t("alertWidgetAIContentTemplates.savedTemplates")}
              </Text>
              <ScrollView
                contentContainerStyle={{ paddingHorizontal: ms(12) }}
                horizontal
                showsHorizontalScrollIndicator={false}
              >
                {savedTemplates.map((template) => (
                  <AlertWidgetAIContentTemplates.TemplateItem
                    key={template.id}
                    template={{
                      onPress: () => {
                        setSelectedTemplate(template);
                      },
                      onDelete: () => onDeleteTemplate(template),
                      emoji: template.emoji,
                      title: template.title,
                    }}
                  />
                ))}
              </ScrollView>
            </>
          )}
          <View style={styles.largerSpacer} />
        </>

        <View style={{ paddingHorizontal: ms(24) }}>
          <Input
            label=""
            value={selectedTemplate?.title || ""}
            onTextChange={(text) => {
              setSelectedTemplate({
                ...selectedTemplate,
                title: text,
              });
            }}
            placeholder={t("alertWidgetAIContentTemplates.inputPlaceholder")}
          />
          <View style={styles.spacer} />

          <select
            style={SELECT_STYLES}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {AUDIO_LANGUAGES.map((language) => (
              <option key={language.value} value={language.value}>
                {language.label}
              </option>
            ))}
          </select>

          <View style={styles.spacer} />
          <Input
            label=""
            value={selectedTemplate?.prompt || ""}
            height={height * 0.4}
            onTextChange={(text) => {
              setSelectedTemplate({
                ...selectedTemplate,
                prompt: text,
              });
            }}
            multiLine
            placeholder={t("alertWidgetAIContentTemplates.promptPlaceholder")}
          />
        </View>
        <View style={styles.largerSpacer} />
      </ScrollView>

      <PrimaryButton
        loading={isLoading}
        text={t("alertWidgetAIContentTemplates.save")}
        onPress={onSaveTemplate}
      />
    </ModalContainer>
  );
};

AlertWidgetAIContentTemplates.TemplateItem = ({
  template,
}: {
  template: {
    onDelete?: () => void;
    onPress: () => void;
    emoji: string;
    title: string;
  };
}) => {
  const width = useUISlice((state) => state.device.width);

  return (
    <Pressable
      style={{
        width: Math.min(width * 0.24, ms(80)),
        justifyContent: "center",
        alignItems: "center",
        gap: ms(8),
        marginRight: ms(16),
      }}
      onPress={template.onPress}
    >
      <View
        style={{
          backgroundColor: Colors.white,
          width: ms(48),
          height: ms(48),
          borderRadius: ms(48),
          borderWidth: 1,
          borderColor: Colors.text + "20",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text style={{ fontSize: ms(16) }}>{template.emoji}</Text>
      </View>

      <Text
        numberOfLines={2}
        style={{
          maxWidth: width * 0.21,
          fontSize: ms(12),
          textAlign: "center",
          fontWeight: "400",
          color: Colors.text,
        }}
      >
        {template.title}
      </Text>

      {Boolean(template.onDelete) && (
        <Pressable
          style={{
            position: "absolute",
            backgroundColor: Colors.error,
            borderRadius: ms(48),
            top: ms(0),
            right: ms(8),
            width: ms(20),
            height: ms(20),
            alignItems: "center",
            justifyContent: "center",
          }}
          onPress={template.onDelete}
        >
          <Icon
            icon="close"
            style={{ tintColor: Colors.white, width: ms(16), height: ms(16) }}
          />
        </Pressable>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
  largerSpacer: {
    height: ms(32),
  },
  label: {
    paddingHorizontal: ms(24),
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
    marginBottom: ms(8),
  },
  negativeMargin: {
    marginBottom: -ms(16),
  },
});

export default AlertWidgetAIContentTemplates;
